
/**
 * @component UserSelecter
 *
 * @description
 * 这是一个审批用户选择列表组件
 *
 * @props
 * | Name       | Type               | Description                           |
 * | ---------- | ------------------ | ------------------------------------- |
 * | modelValue | `UserSelectItem[]` | 选中的审批用户列表                      |
 * | priview    | `Boolean`          | 是否预览模式,预览模式将隐藏选择按钮      |
 * 
 *
 * @example
 * ```vue
 * <UserSelect v-model="member_list" :priview="true" />
 * ```
 */

import { ref, reactive, onMounted, unref, PropType, watch, defineComponent } from 'vue';

/* Npm Plugins */
import { ElMessage } from 'element-plus';
import { Search } from '@element-plus/icons-vue'

/* Plugins */
import { GbTables } from "@/components/GbTables"
import { GbDialog } from '@/components/GbDialog/Index';

/* Hooks & Utils */
import { useGbTable } from '@/hooks/useGbTable'
import { findIndex } from '../../../utils/index';
import { propTypes } from '@/utils/propTypes';

/* Apis */
import { UserSelectListApi } from '../../../apis/accounts/accounts.api';

/* Types */
import type { TableColumn } from '@/components/GbTables/GbTablesTypes'
import type { UserSearch, UserSelectItem } from '../../../apis/accounts/accounts.type';

export default defineComponent({
    Name: "UserSelecter",
    components: { GbTables, GbDialog, Search },
    props: {
        modelValue: {
            type: Object as PropType<Nullable<UserSelectItem[]>>,
            default: () => null
        },
        userId: propTypes.string.def(''),
        priview: propTypes.bool.def(false),
        maxCount: propTypes.number.def(10)
    },
    emits: ['update:modelValue', 'update:userId', 'selected'],
    setup(props, { slots, expose, emit }) {

        const selectText = ref<string>('')
        const loading = ref<boolean>(false);
        const showDialog = ref<boolean>(false)

        /* 数据Table绑定设置 */
        const tabColumns = reactive<TableColumn[]>([
            { field: 'id', label: '用户ID', columnWidth: "100" },
            { field: 'name', label: '姓名', columnWidth: "100", },
            { field: 'sex', label: '性别', columnWidth: "80" },
            { field: 'dept_name', label: '部门名称' },
            { field: 'action', width: '80px', label: "操作", fixed: 'left', align: 'center' },
        ])

        /* 控制变量 */
        const search = reactive<UserSearch>({
            current: 1,
            page_size: 10,
        })

        const selectList = ref<UserSelectItem[]>([])

        const { register, tableObject, methods } = useGbTable<UserSelectItem>({
            getListApi: UserSelectListApi,
            response: {
                data: 'data',
                total: 'total'
            }
        })

        const { setSearchParams } = methods

        /* 组件内方法 */
        const pMethods = {

            /* 查询的值更改 */
            onInputChange: (val: any) => {

                unref(search).search_key = val

                setSearchParams(search)

            },

            /* 打开选择窗口 */
            onDialogBtnClick: () => {

                showDialog.value = true

                unref(search).search_key = ""

                setSearchParams(search)

            },

            /* 点击确认按钮 */
            onSureBtnClick: () => {

                loading.value = true

                emit('selected', unref(selectList))

                /* 为什么会有这段代码？这是在营造一种：“前端我啊，在努力工作呢”的感觉，毕竟闪太快会给别人觉得有问题 */
                setTimeout(() => {

                    loading.value = false

                    showDialog.value = false

                }, 500);

            },

            /* 删除已选择用户 */
            onTagClosed: (tag: UserSelectItem) => {

                unref(selectList).splice(unref(selectList).indexOf(tag), 1)

                emit('update:modelValue', unref(selectList))

            },

            /* 选择用户（理论上审批最多只有[props.maxCount]个用户，审批应该不用这么多人吧,2333） */
            selectUser: (user: UserSelectItem) => {

                if ((unref(selectList) || []).length >= props.maxCount) {

                    ElMessage.warning(`最多只能选择${props.maxCount}个用户。`)

                }
                else {

                    const list = unref(selectList) || []

                    const rowIndex = findIndex(list, (t: any) => { return t.id == user.id })

                    if (rowIndex >= 0) {

                        ElMessage.warning(`员工：${user.name}(${user.id})，已在选择列表当中。`)

                    }
                    else {

                        unref(selectList).push(user)

                    }
                }

            }
        }


        watch(() => props.modelValue, (list: any) => {

            selectList.value = list || [];

        }, { deep: true, immediate: true })

        watch(() => props.userId, async (userNo: string) => {

            if (userNo) {

                const { data } = await UserSelectListApi({ search_key: userNo, current: 1, page_size: 1 })

                if (data && (data.data || []).length >= 1) {

                    selectList.value = [data.data[0]]

                }
            }
            // selectList.value = list || [];

        }, { deep: true, immediate: true })

        /* 选择列表有更改的时候 */
        watch(() => unref(selectList), (nVal: UserSelectItem[]) => {

            if ((nVal || []).length > 0) {

                selectText.value = nVal.map((item: UserSelectItem) => { return item.name }).join(',')

            } else {

                selectText.value = '';

            }

        }, { deep: true, immediate: true })

        onMounted(() => { setSearchParams(search) })

        return {
            props,
            Search,
            selectText,
            showDialog,
            loading,
            search,
            selectList,
            tableObject,
            tabColumns,
            register,
            setSearchParams,
            pMethods
        }
    },
})

